import {
  Button,
  Card,
  IconButton,
  Stack,
  Typography,
  MenuItem,
  FormControl,
  Select,
  Box,
  Grid,
  TextField,
  Divider,
  Avatar,
  ListItemAvatar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./quill.css";
import ShareIcon from "@mui/icons-material/Share";
import Image from "../../../components/Image";
import { IssueType } from "../../../services/stickerColor";
import axiosInstance from "../../../utilities/axios";
import { useMessage } from "../../../providers/Provider";
import CopyIssueLink from "./CopyIssueLink";
import { escapeDanger, formatTimeAgo } from "../../../utilities/function";
import DeleteIcon from "@mui/icons-material/Delete";
import Comments from "./Comments";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";

const CreateIssues = (props) => {
  const {
    task,
    closeProject,
    ProjectId,
    selectedIssue,
    fetchIssues,
    setProjectState,
  } = props;

  // UI state
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [name, setName] = useState(task.title || "");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // *** NOTICE: We store assignee/reporter as ObjectId strings, not emails ***
  const [selectOption, setSelectOption] = useState({
    status: task.status || "Backlog",
    priority: task.priority || "Medium",
    issueType: task.type || "Task",
    assignee: task.assignee?._id || "", // <— use _id here
    reporter: task.reporter?._id || "", // <— use _id here
  });

  const [showDescription, setShowDescription] = useState(false);
  const [showTitle, setShowTitle] = useState(false);
  const [text, setText] = useState(task.description || "");

  const { showError, showSuccess } = useMessage();
  const [organizationId, setOrganizationId] = useState("");
  const [organizationUser, setOrganizationUser] = useState([]);

  // Pull selectedOrganization from localStorage
  useEffect(() => {
    const id = window.localStorage.getItem("selectedOrganization");
    setOrganizationId(id);
  }, []);

  // Fetch organization members (array of { _id, email, firstName, lastName, … })
  const fetchOrganizationUsers = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `/api/v2/organizations/${organizationId}/members`
      );
      setOrganizationUser(response.data.members);
    } catch (e) {
       // console.error("Failed to fetch organization users:", e);
    }
  }, [organizationId]);

  useEffect(() => {
    if (organizationId) {
      fetchOrganizationUsers();
    }
  }, [fetchOrganizationUsers, organizationId]);

  // Whenever `task` prop changes, reset local form state
  useEffect(() => {
    setName(task.title || "");
    setText(task.description || "");
    setSelectOption((prev) => ({
      ...prev,
      status: task.status || "Backlog",
      priority: task.priority || "Medium",
      issueType: task.type || "Task",
      assignee: task.assignee?._id || "", // <— reset to user’s _id
      reporter: task.reporter?._id || "", // <— reset to user’s _id
    }));
  }, [task]);

  // Quill configuration
  const quillRef = useRef(null);
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike", "blockquote"],
          [
            { list: "ordered" },
            { list: "bullet" },
            { indent: "-1" },
            { indent: "+1" },
          ],
          ["link", "image"],
          ["clean"],
        ],
      },
    }),
    []
  );
  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
  ];

  // Calculate "time ago"
  const currentTime = new Date();
  const issueTime = new Date(task.createdAt);
  const differenceInMilliseconds = currentTime - issueTime;
  const formattedTimeAgo = formatTimeAgo(differenceInMilliseconds);

  // Generic handler for updating any field in selectOption
  const handleChangeOption = (e) => {
    const { name, value } = e.target;
    setSelectOption((prev) => ({ ...prev, [name]: value }));
  };

  // Quill’s onChange
  const handleChangeQuill = (value) => {
    setText(value);
  };

  // Title edit handlers
  const handleTitleSave = () => {
    setShowTitle(false);
  };
  const handleTitleCancel = () => {
    setShowTitle(false);
    setName(task.title || "");
  };

  // Description edit handlers
  const handleDescriptionSave = () => {
    setShowDescription(false);
  };
  const handleDescriptionCancel = () => {
    setShowDescription(false);
    setText(task.description || "");
  };

// Move file function
const moveFileToFolder = async (resourceId, folderId, ownerId) => {
  try {
    await axiosInstance.post(
      'https://api.files.clikkle.com:4500/api/fs/move',
      {
        newParentId: folderId,
        resourceId,
        userId: ownerId
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    //  // console.log(`Moved file ${resourceId} to folder ${folderId}`);
  } catch (error) {
     // console.error('Error moving file:', error);
    // Implement retry logic or user notification here
  }
};

// Update issue function
const updateIssue = async (e) => {
  e.preventDefault();
  setLoading(true);
  
  const user = JSON.parse(localStorage.getItem("user"));
  const org = JSON.parse(localStorage.getItem("org"));
  
  try {
    const response = await axiosInstance.put(
      `/api/v2/projects/${ProjectId}/issues/${selectedIssue}/`,
      {
        assignee: selectOption.assignee,
        reporter: selectOption.reporter,
        status: selectOption.status,
        priority: selectOption.priority,
        issueType: selectOption.issueType,
        description: text,
        name,
        ownerId: org.ownerId,
        ownerEmail: user.recoveryEmail
      }
    );
     // console.log(response.data.uploadedResources,"response.data.uploadedResources");
    
    // Move uploaded files after successful issue update
    if (response.data.uploadedResources?.length > 0) {
      response.data.uploadedResources.forEach(resourceId => {
        moveFileToFolder(
          resourceId, 
          org.folderId,  // From user context
          org.ownerId     // Organization owner ID
        );
      });
    }

    // Close modal and refresh data
    setProjectState(false);
    fetchIssues();  // Refresh issue list
    
  } catch (error) {
     // console.error("Error updating issue:", error);
    // Show error notification to user
    alert(`Update failed: ${error.response?.data?.message || error.message}`);
  } finally {
    setLoading(false);
  }
};
  // Delete issue
  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await axiosInstance.delete(`/api/v2/issues/${selectedIssue}`);
      fetchIssues();
      showSuccess("Issue deleted successfully");
      setProjectState(false);
    } catch (error) {
       // console.error(error);
      showError("Failed to delete issue", error);
    } finally {
      setDeleteLoading(false);
      setConfirmDeleteDialogOpen(false);
    }
  };
  function stringAvatar(name) {
    return {
      children: `${name.split(" ")[0][0]}${name.split(" ")[1][0]}`,
    };
  }
  return (
    <>
      <Card
        sx={{
          position: "relative",
          boxShadow: "rgba(0, 0, 0, 0.45) 0px 25px 20px -20px",
          borderRadius: "8px",
          maxWidth: "1250px",
          border: 2,
          borderColor: "rgba(255, 255, 255, 0.07)",
          width: "100%",
          p: 2,
          mx: 2,
          height: { xs: "85vh", xl: "70vh" },
          overflowY: "auto",
          pb: 2,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Box display="flex" alignItems="center">
            <Image
              name={
                selectOption.issueType
                  ? IssueType[selectOption.issueType]
                  : "Task.png"
              }
              sx={{ width: 16, height: 16 }}
            />
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{ ml: 1 }}
              color="text.secondary"
            >
              {selectOption.status}
            </Typography>
          </Box>
          <Box>
            <IconButton onClick={() => setOpen(true)} disableRipple>
              <ShareIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => setConfirmDeleteDialogOpen(true)}
              disableRipple
            >
              <DeleteIcon sx={{ mx: 0.3 }} fontSize="small" />
            </IconButton>
            <IconButton onClick={closeProject} disableRipple>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Stack>

        <form onSubmit={updateIssue}>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {/* LEFT SIDE: TITLE + DESCRIPTION */}
            <Grid item lg={8} xs={12}>
              <Box
                sx={{
                  p: 2,
                  maxHeight: { xs: "60vh", xl: "50vh" },
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                {/* ===== TITLE ===== */}
                <Box sx={{ mb: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.tertiary"
                    sx={{ pl: 0.8 }}
                  >
                    Title
                  </Typography>
                  {showTitle ? (
                    <>
                      <TextField
                        name="name"
                        size="small"
                        fullWidth
                        placeholder="Enter Title"
                        multiline
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        margin="none"
                      />
                      <Box
                        display="flex"
                        justifyContent="flex-end"
                        mb={0}
                        pb={0}
                      >
                        <IconButton
                          sx={{
                            mt: 0,
                            width: 25,
                            height: 25,
                            backgroundColor: "background.default",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "4px",
                            mr: 0.3,
                          }}
                          onClick={handleTitleSave}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          sx={{
                            width: 25,
                            height: 25,
                            backgroundColor: "background.default",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "4px",
                          }}
                          onClick={handleTitleCancel}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </>
                  ) : (
                    <Typography
                      onClick={() => setShowTitle(true)}
                      sx={{
                        maxWidth: "100%",
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        py: 1,
                        pl: 0.8,
                        borderRadius: "4px",
                        "&:hover": {
                          backgroundColor: "white.light",
                        },
                      }}
                      color="text.tertiary"
                      variant="h6"
                    >
                      {name || "Add a Title..."}
                    </Typography>
                  )}
                </Box>

                {/* ===== DESCRIPTION ===== */}
                <Box mt={1}>
                  <Typography
                    variant="caption"
                    color="text.tertiary"
                    sx={{ pl: 0.8 }}
                  >
                    Description
                  </Typography>
                  <Box mt={1} width="100%">
                    {showDescription ? (
                      <>
                        <ReactQuill
                          theme="snow"
                          value={text}
                          modules={modules}
                          formats={formats}
                          onChange={handleChangeQuill}
                          ref={quillRef}
                          className=".richtextWrap"
                        />
                        <Box mt={1}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleDescriptionSave}
                          >
                            Save
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{ ml: 1 }}
                            onClick={handleDescriptionCancel}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </>
                    ) : (
                      <Box sx={{ width: "100%" }}>
                        <Typography
                          onClick={() => setShowDescription(true)}
                          color="text.tertiary"
                          sx={{
                            width: "100%",
                            fontSize: "14px",
                            py: 1,
                            pl: 0.8,
                            borderRadius: "4px",
                            "&:hover": {
                              backgroundColor: "white.light",
                            },
                          }}
                        >
                          {text ? (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: escapeDanger(
                                  text.replace(
                                    /<img/g,
                                    '<img style="width: 100%; max-width: 400px; height: auto; display:block; padding:5px"'
                                  )
                                ),
                              }}
                            />
                          ) : (
                            "Add a description..."
                          )}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box mt={10}>
                  <Divider />
                  <Comments   projectId={ProjectId} issueId={selectedIssue}/>
                </Box>
              </Box>
            </Grid>

            {/* RIGHT SIDE: DETAILS */}
            <Grid item lg={4} xs={12}>
              <Box
                sx={{
                  border: 1,
                  borderColor: "rgba(255, 255, 255, 0.07)",
                }}
              >
                <Box p={1}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight={500}
                  >
                    Details
                  </Typography>
                </Box>
                <Divider />

                <Box p={2}>
                  <Grid container spacing={2}>
                    {/* ===== ASSIGNEE ===== */}
                    <Grid item xs={4}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={500}
                      >
                        Assignee
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FormControl size="small" fullWidth>
                        <Select
                          size="small"
                          displayEmpty
                          name="assignee"
                          value={selectOption.assignee}
                          onChange={handleChangeOption}
                          renderValue={(value) => {
                            if (!value) {
                              return (
                                <Box display="flex" alignItems="center">
                                  <Avatar
                                    sx={{ height: 25, width: 25, mr: 1 }}
                                  />
                                  <em>Unassigned</em>
                                </Box>
                              );
                            }
                            // find chosen user by _id
                            const chosenUser = organizationUser.find(
                              (u) => u._id === value
                            );
                            return (
                              <Box display="flex" alignItems="center">
                                <Avatar
                                  sx={{ height: 25, width: 25, mr: 1 }}
                                  src={chosenUser?.profilePhoto || ""}
                                />
                                {chosenUser
                                  ? `${chosenUser.firstName} ${chosenUser.lastName}`
                                  : value}
                              </Box>
                            );
                          }}
                        >
                          <MenuItem value="">
                            <Box display="flex" alignItems="center">
                              <Avatar sx={{ height: 30, width: 30, mr: 1 }} />
                              <em>Unassigned</em>
                            </Box>
                          </MenuItem>
                          {organizationUser.map((member) => (
                            <MenuItem key={member._id} value={member._id}>
                              <Box display="flex" alignItems="center">
                                <Avatar
                                  sx={{ height: 30, width: 30, mr: 1 }}
                                  src={member.profilePhoto || ""}
                                />
                                {member.firstName} {member.lastName}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* ===== PRIORITY ===== */}
                    <Grid item xs={4}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={500}
                      >
                        Priority
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FormControl size="small" fullWidth>
                        <Select
                          size="small"
                          value={selectOption.priority || ""}
                          name="priority"
                          onChange={handleChangeOption}
                          renderValue={(selected) => (
                            <Box display="flex" alignItems="center">
                              <Image
                                name={
                                  selected
                                    ? `${selected.toLowerCase()}.png`
                                    : "default.png"
                                }
                                sx={{ width: 16, height: 16, mr: 1 }}
                              />
                              {selected || "None"}
                            </Box>
                          )}
                        >
                          <MenuItem value="Lowest">
                            <Image
                              name="lowest.png"
                              sx={{ width: 16, height: 16, mr: 1 }}
                            />
                            Lowest
                          </MenuItem>
                          <MenuItem value="Low">
                            <Image
                              name="low.png"
                              sx={{ width: 16, height: 16, mr: 1 }}
                            />
                            Low
                          </MenuItem>
                          <MenuItem value="Medium">
                            <Image
                              name="medium.png"
                              sx={{ width: 16, height: 16, mr: 1 }}
                            />
                            Medium
                          </MenuItem>
                          <MenuItem value="High">
                            <Image
                              name="high.png"
                              sx={{ width: 16, height: 16, mr: 1 }}
                            />
                            High
                          </MenuItem>
                          <MenuItem value="Highest">
                            <Image
                              name="highest.png"
                              sx={{ width: 16, height: 16, mr: 1 }}
                            />
                            Highest
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* ===== REPORTER ===== */}
                    <Grid item xs={4}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={500}
                      >
                        Reporter
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FormControl size="small" fullWidth>
                        <Select
                          size="small"
                          displayEmpty
                          name="reporter"
                          value={selectOption.reporter}
                          onChange={handleChangeOption}
                          renderValue={(value) => {
                            if (!value) {
                              return (
                                <Box display="flex" alignItems="center">
                                  <Avatar
                                    sx={{ height: 25, width: 25, mr: 1 }}
                                  />
                                  <em>Unassigned</em>
                                </Box>
                              );
                            }
                            const chosenUser = organizationUser.find(
                              (u) => u._id === value
                            );
                            return (
                              <Box display="flex" alignItems="center">
                                <Avatar
                                  sx={{ height: 25, width: 25, mr: 1 }}
                                  src={chosenUser?.profilePhoto || ""}
                                />
                                {chosenUser
                                  ? `${chosenUser.firstName} ${chosenUser.lastName}`
                                  : value}
                              </Box>
                            );
                          }}
                        >
                          <MenuItem value="">
                            <Box display="flex" alignItems="center">
                              <Avatar sx={{ height: 30, width: 30, mr: 1 }} />
                              <em>Unassigned</em>
                            </Box>
                          </MenuItem>
                          {organizationUser.map((member) => (
                            <MenuItem key={member._id} value={member._id}>
                              <ListItemAvatar>
                                <Avatar
                                  sx={{ height: 30, width: 30, mr: 1 }}
                                  src={member.profilePhoto || ""}
                                />
                              </ListItemAvatar>
                              {member.firstName} {member.lastName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* ===== ISSUE TYPE ===== */}
                    <Grid item xs={4}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={500}
                      >
                        Issue Type
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FormControl size="small" fullWidth>
                        <Select
                          size="small"
                          displayEmpty
                          name="issueType"
                          value={selectOption.issueType || ""}
                          onChange={handleChangeOption}
                          renderValue={(selected) => (
                            <Box display="flex" alignItems="center">
                              <Image
                                name={IssueType[selected] || "default.png"}
                                sx={{ width: 16, height: 16, mr: 1 }}
                                />
                                {/* { // console.log(selected,"selected")} */}
                              {selected || "None"}
                            </Box>
                          )}
                        >
                          <MenuItem value="">
                            <Image
                              name="default.png"
                              sx={{ width: 16, height: 16, mr: 1 }}
                            />
                            None
                          </MenuItem>
                          {Object.keys(IssueType).map((issueTypeKey) => (
                            <MenuItem key={issueTypeKey} value={issueTypeKey}>
                              <Image
                                name={IssueType[issueTypeKey]}
                                sx={{ width: 16, height: 16, mr: 1 }}
                              />
                              {issueTypeKey}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* ===== STATUS ===== */}
                    <Grid item xs={4}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={500}
                      >
                        Status
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FormControl size="small" fullWidth>
                        <Select
                          size="small"
                          displayEmpty
                          name="status"
                          value={selectOption.status || ""}
                          onChange={handleChangeOption}
                          renderValue={(selected) => (
                            <Box display="flex" alignItems="center">
                              <Typography variant="body2" color="text.tertiary">
                                {selected || "None"}
                              </Typography>
                            </Box>
                          )}
                        >
                          <MenuItem value="">
                            <Typography variant="body2" color="text.tertiary">
                              None
                            </Typography>
                          </MenuItem>
                          <MenuItem value="Backlog">
                            <Typography variant="body2">Backlog</Typography>
                          </MenuItem>
                          <MenuItem value="To Do">
                            <Typography variant="body2">To Do</Typography>
                          </MenuItem>
                          <MenuItem value="In Progress">
                            <Typography variant="body2">In Progress</Typography>
                          </MenuItem>
                          <MenuItem value="Review">
                            <Typography variant="body2">Review</Typography>
                          </MenuItem>
                          <MenuItem value="Done">
                            <Typography variant="body2">Done</Typography>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              </Box>

              {/* Created Date */}
              <Box mt={2}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  Created Date: {new Date(task.createdAt).toLocaleString()}
                </Typography>
              </Box>
              <Box mt={2}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  Created At: {formattedTimeAgo}
                </Typography>
              </Box>

              {/* Submit Button */}
              <Box sx={{ mt: 2, textAlign: "right" }}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  endIcon={
                    loading && (
                      <CircularProgress size="20px" sx={{ color: "inherit" }} />
                    )
                  }
                  type="submit"
                >
                  Submit
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Card>

      {/* CONFIRM DELETE DIALOG */}
      <Dialog
        open={confirmDeleteDialogOpen}
        onClose={() => setConfirmDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this Task?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeleteDialogOpen(false)}
            color="primary"
            sx={{ color: "#fff" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={deleteLoading}
            endIcon={
              deleteLoading && (
                <CircularProgress size="20px" sx={{ color: "inherit" }} />
              )
            }
            sx={{ backgroundColor: "#ff2121" }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* COPY ISSUE LINK DIALOG */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <CopyIssueLink handleClose={() => setOpen(false)} />
      </Dialog>
    </>
  );
};

export default CreateIssues;
